import { useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useOutsideClick } from "@script/script";
import type { GroupInfo } from "@vTypes";
import { images } from "@/assets/img/imagesMap";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import {
  useIsAdmin,
  useIsGroupNurse,
  useLoginUser,
  useAuthActions,
} from "@/hooks/useAuth";
import { useGroupNurseGroupsQuery } from "@/hooks/useGroupNurseGroupsQuery";
// import httpClient from "@/config/axios"; // TODO: 토큰 변경 API 구현 시 사용
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ChevronDown, Loader2 } from "lucide-react";
import { compareGroupNames } from "@/utils/sort-util";
import { useNavigate } from "react-router-dom";

type Props = {
  onClickAdd: () => void;
  openAddGroup: boolean;
  showAddButton?: boolean; // nav 전용 스타일을 위해서 추가
  nav?: boolean; // 그룹 메뉴 안보이게 하기 위해서 추가
  hiddenGroup?: boolean;
  onEdit?: (group: GroupInfo) => void;
};

export default function GroupList({
  onClickAdd,
  nav = false,
  hiddenGroup = false,
  onEdit,
}: Props) {
  const [isGroupListOpend, setIsGroupListOpend] = useState<boolean>(false);
  const groupListRef = useRef<HTMLDivElement | null>(null);
  const {
    groups,
    selectedGroupId,
    setSelectedGroupId: onSelect,
  } = useGroupSelection();
  const isAdmin = useIsAdmin();
  const isGroupNurse = useIsGroupNurse();
  const loginUser = useLoginUser();
  const { setLoginUser } = useAuthActions();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useOutsideClick(
    groupListRef as unknown as React.RefObject<HTMLElement>,
    () => {
      setIsGroupListOpend(false);
    }
  );

  const { data: groupNurseGroups } = useGroupNurseGroupsQuery(isGroupNurse);
  const rawGroupDatas = isAdmin ? groups : groupNurseGroups;

  // group_name 기준으로 가나다순(한글 우선), 그 다음 숫자순 정렬
  const groupDatas = useMemo(() => {
    if (!rawGroupDatas) return rawGroupDatas;

    return [...rawGroupDatas].sort((a, b) => {
      return compareGroupNames(a.group_name || "", b.group_name || "");
    });
  }, [rawGroupDatas]);

  // 여기서 그룹 네임 반환함
  const selectedName = (() => {
    const targetGroups = groupDatas ?? [];
    const found = targetGroups.find((g) => g.group_id === selectedGroupId);
    return found
      ? found.group_name
      : (targetGroups[0]?.group_name ?? "그룹 없음");
  })();

  // Admin
  const handleGroupSelect = (group_id: string) => {
    queryClient.clear();
    onSelect(group_id);
    setIsGroupListOpend(false);
    // 그룹 변경 시 루트로 이동
    navigate("/");
  };

  const { mutateAsync: changeToken, isPending: isChangingToken } = useMutation({
    mutationFn: async (group_id: string) => {
      return await httpClient.post<{
        message: string;
        group_id: string;
        group_name: string;
      }>("/auth/switch-group", {
        target_group_id: group_id,
      });
    },
    onSuccess: (data) => {
      onSelect(data.group_id);

      if (loginUser) {
        setLoginUser({
          ...loginUser,
          group_id: data.group_id,
          mb_part_name: data.group_name,
        });
      }

      queryClient.clear();
      setIsGroupListOpend(false);
      toast.success("그룹을 전환하였습니다.");
      // 그룹 변경 시 루트로 이동
      navigate("/");
    },
  });

  // 그룹관리자 - 이땐 Token 변경해야함
  // Token 변경 프로세스
  const handleGroupNurseSelect = async (group_id: string) => {
    await changeToken(group_id);
  };

  const role = (() => {
    if (isAdmin) return "ADMIN";
    else if (isGroupNurse) return "HN";
    else return "NURSE";
  })();

  const portalRoot = document.getElementById("change-group-portal");

  const loadingOverlay =
    isChangingToken && portalRoot
      ? createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30  "
            style={{
              backdropFilter: "blur(2px)",
            }}
          >
            <div className="flex flex-col items-center justify-center px-6 py-4 rounded-lg">
              <Alert variant="default" className="shadow-2xl">
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertTitle>그룹 변경 중입니다.</AlertTitle>
                <AlertDescription>잠시만 기다려주세요.</AlertDescription>
              </Alert>
            </div>
          </div>,
          portalRoot
        )
      : null;

  return (
    <>
      {/* Portal */}
      {loadingOverlay}
      {!hiddenGroup && (
        <div className="mr-auto flex w-full items-center gap-2">
          <div
            id="bookmarks-container"
            className="relative w-full"
            ref={groupListRef}
          >
            <button
              type="button"
              className={`flex w-full items-center justify-between gap-3 border bg-white text-left font-pretendard shadow-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 ${
                nav
                  ? "h-[68px] rounded-[10px] border-2 border-sig px-5 text-[17px] text-sig"
                  : "h-11 rounded-lg border-[#D6D6DE] px-3 text-[15px] text-[#344054]"
              }`}
              onClick={() => {
                setIsGroupListOpend((prev) => !prev);
              }}
            >
              <span
                className={`block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap ${nav && "text-sig font-semibold"}`}
                title={selectedName}
              >
                {selectedName}
              </span>
              <ChevronDown
                aria-hidden="true"
                className={`size-4 shrink-0 transition-transform ${
                  isGroupListOpend ? "rotate-180" : ""
                } ${nav ? "text-sig" : "text-[#667085]"}`}
              />
            </button>
            {isGroupListOpend && (
              <div className="absolute inset-x-0 top-full z-20 mt-2 max-h-[50vh] overflow-hidden rounded-lg border border-[#D6D6DE] bg-white py-1 shadow-[0_8px_20px_rgba(16,24,40,0.14)]">
                <div className="thin-scrollbar max-h-[50vh] overflow-y-auto">
                  {isAdmin && (
                    <button
                      type="button"
                      className=" font-medium block w-full font-pretendard
                      whitespace-nowrap  text-ellipsis z-[1] !text-[17px] text-[#0E59C6] ] !pr-[8px] !py-[6px] hover:bg-[#F0F0F2]"
                      onClick={() => {
                        onClickAdd();
                        setIsGroupListOpend(false);
                      }}
                    >
                      <span className="font-pretendard">+부서(병동)추가</span>
                    </button>
                  )}

                  {(groupDatas ?? []).map((item) => {
                    const isActive = item.group_id === selectedGroupId;

                    return (
                      <div
                        key={item.group_id}
                        className={`flex w-full items-center font-pretendard text-base ${isActive ? "bg-[#dbe9ff]" : ""}`}
                      >
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center px-[21px] py-1 text-left font-normal text-gray-800 hover:bg-[#F0F0F2]"
                          aria-current={isActive ? "true" : undefined}
                        onClick={() => {
                          switch (role) {
                            case "ADMIN":
                              handleGroupSelect(item.group_id);
                              break;
                            case "HN":
                              handleGroupNurseSelect(item.group_id);
                              break;
                          }
                        }}
                      >
                        <div className="truncate">
                          <span className="font-pretendard ">
                            {item.group_name}
                          </span>
                        </div>
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            aria-label={`${item.group_name} 병동 수정`}
                            onClick={() => {
                              onEdit?.(item);
                              setIsGroupListOpend(false);
                            }}
                            className="mr-2 flex size-8 shrink-0 items-center justify-center rounded-md hover:bg-[#F0F0F2]"
                          >
                            <img
                              src={images.icon_pencil}
                              alt=""
                              aria-hidden="true"
                              className="hover:opacity-70"
                            />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
